// # Sub1 Subscription Model

import { not, notEmpty, empty, and, equal, or } from '@ember/object/computed';
import { isNone } from '@ember/utils';
import { computed } from '@ember/object';
import { notEqual } from '@square/dashboard-utilities/macros';
import Enum from '@square/dashboard-utilities/utils/enum';
import { attr } from '@ember-data/model';
import EmberDataBaseModel from '@square/dashboard-data/models/ember-data-base-model';
import moment from 'moment';
import { inject as service } from '@ember/service';
const SubscribedStatuses = ['active', 'free_trial', 'in_grace'];
const ResubscribableStatuses = ['canceled', 'in_grace', 'delinquent'];
const PreviouslySubscribedStatuses = ['canceled', 'delinquent', 'terminated'];
const UnsubscribedStatuses = PreviouslySubscribedStatuses.concat(['inactive', 'pending']);
const Statuses = new Enum(SubscribedStatuses.concat(UnsubscribedStatuses));
const ProductTypes = new Enum(['external', 'internal']);
const PaymentMethods = new Enum(['payout_adjustment', 'card', 'none']);
export default EmberDataBaseModel.extend({
  intl: service(),
  service: attr('string'),
  status: attr('enum', {
    enumClass: Statuses,
    defaultValue: Statuses.inactive
  }),
  serviceStartDate: attr('string'),
  serviceEndDate: attr('string'),
  features: attr(),
  // eslint-disable-line ember/no-empty-attrs
  signupForwardTarget: attr('string'),
  nextFee: attr(),
  // eslint-disable-line ember/no-empty-attrs
  feeAmount: attr('money-proto'),
  lastFee: attr(),
  // eslint-disable-line ember/no-empty-attrs
  billingCycle: attr('string'),
  productGroup: attr('string'),
  productName: attr('string'),
  productTier: attr('number'),
  iconLarge: attr('string'),
  moreInfoLink: attr('string'),
  termsOfUseLink: attr('string'),
  freeTrialEndDate: attr('string'),
  freeTrialDays: attr('number'),
  paymentMethod: attr('string'),
  productType: attr('enum', {
    enumClass: ProductTypes
  }),
  allowForMultiunit: attr('boolean'),
  subscriptionProductVersion: attr('number'),
  requirePaymentMethodOnSignup: attr('boolean'),
  externalActivationUrl: attr('string'),
  // URL used instead of initiating subscription modal flow
  signupExternalTarget: attr('string'),
  // After activating, external (to dashboard) URL used for redirect
  allowActivation: attr('boolean'),
  userToken: attr('string'),
  webToken: attr('string'),
  translations: attr('boolean'),
  canNotSubscribeFromPricingPage: attr('boolean'),
  allowSignUp: notEqual('status', Statuses.terminated),
  notAllowSignUp: not('allowSignUp'),
  isFreeTrial: notEmpty('freeTrialEndDate'),
  // TODO: johnweathers - remove this property when all EventStream 1 events in Dashboard are gone
  eventOptions: computed('service', function () {
    return {
      label: this.service
    };
  }),
  hasEndDate: notEmpty('serviceEndDate'),
  hasNoEndDate: not('hasEndDate'),
  hasNotBeenCharged: empty('lastFee'),
  activeButCanceled: and('statusIsActive', 'hasEndDate'),
  activeAndNotCanceled: and('statusIsActive', 'hasNoEndDate'),
  freeTrialButCanceled: and('isFreeTrial', 'activeButCanceled'),
  isInternalProduct: equal('productType', ProductTypes.internal),
  isExternalProduct: equal('productType', ProductTypes.external),
  statusIsInactive: equal('status', Statuses.inactive),
  statusIsInGrace: equal('status', Statuses.in_grace),
  statusIsDelinquent: equal('status', Statuses.delinquent),
  statusIsCanceled: equal('status', Statuses.canceled),
  unsubscribed: not('statusIsActive'),
  statusIsActivePendingCancellation: computed('status', 'hasEndDate', function () {
    return this.status === Statuses.active && this.hasEndDate;
  }),
  translationKey: computed('productGroup', 'service', 'isExternalProduct', function () {
    if (['loyalty', 'marketing'].includes(this.productGroup)) {
      return this.productGroup;
    } else if (this.isExternalProduct && !isNone(this.productGroup)) {
      return this.productGroup;
    } else {
      return this.service;
    }
  }),
  // Group properties
  lowestTier: equal('productTier', 0),
  partOfProductGroup: notEmpty('productGroup'),
  singleSubscription: not('partOfProductGroup'),
  isVisibleSingle: and('isVisible', 'singleSubscription'),
  // Payment method properties
  nonePaymentMethod: equal('paymentMethod', PaymentMethods.none),
  isFreeTrialWithoutPaymentMethod: and('isFreeTrial', 'nonePaymentMethod'),
  neverSubscribedToService: or('statusIsInactive', 'isFreeTrialWithoutPaymentMethod'),
  isVisible: computed('statusIsActive', 'allowActivation', 'productGroup', 'service', 'isFreeTrialWithoutPaymentMethod', 'hasNoEndDate', function () {
    // TODO (lindsayv) Remove register pro oddness, find real way to not show subscriptions that allow you to subscribe from pending cancel state
    if (this.service === 'register_pro') {
      return this.statusIsActive && this.hasNoEndDate || this.isFreeTrialWithoutPaymentMethod;
    } else {
      return this.statusIsActive || this.allowActivation;
    }
  }),
  hasFreeTrialDays: computed('freeTrialDays', function () {
    return !isNone(this.freeTrialDays) && this.freeTrialDays > 0;
  }),
  canSignUpForFirstFreeTrial: and('hasFreeTrialDays', 'statusIsInactive'),
  canSignUpForFreeTrialNoPaywall: computed('canSignUpForFirstFreeTrial', 'requirePaymentMethodOnSignup', function () {
    return this.canSignUpForFirstFreeTrial && !this.requirePaymentMethodOnSignup;
  }),
  isFree: computed('billingCycle', 'feeAmount.amount', function () {
    return this.billingCycle === 'free' || this.feeAmount?.amount === 0;
  }),
  statusIsActive: computed('status', 'hasFutureStartDate', function () {
    const stat = this.status;
    return stat === Statuses.active || stat === Statuses.free_trial || stat === Statuses.in_grace || this.hasFutureStartDate;
  }),
  hasFutureStartDate: computed('serviceStartDate', 'status', function () {
    return this.status === Statuses.pending && !isNone(this.serviceStartDate) && moment(this.serviceStartDate).endOf('day').isAfter(moment());
  }),
  title: computed('translationKey', 'service', 'translations', function () {
    if (this.translations) {
      // eslint-disable-next-line square/no-translation-key-interpolation
      return this.intl.t(`subscriptions.${this.translationKey}.name`);
    } else {
      return this.service;
    }
  }),
  cancel(reason) {
    // Cancel doesn't necessarily delete the object, so we use a special updateRecord action instead of destroyRecord
    this.reason = reason;
    return this.save({
      adapterOptions: {
        action: 'cancel'
      }
    });
  },
  toCancelRequest() {
    return {
      version: this.subscriptionProductVersion,
      reason: this.reason
    };
  },
  toActivateRequest() {
    return {
      version: this.subscriptionProductVersion,
      // Always ensure we have the latest payment method when activating a subscription
      paymentMethod: this.paymentMethod || PaymentMethods.none
    };
  },
  descriptionStringTemplate: computed('isFreeTrial', 'canSignUpForFreeTrialNoPaywall', 'status', 'isFree', function () {
    return modalType => {
      if (this.isFreeTrial) {
        if (this.canSignUpForFreeTrialNoPaywall) {
          return `subscriptions.${modalType}.descriptionNoPaymentFreeTrial`;
        } else if (UnsubscribedStatuses.includes(this.status)) {
          return `subscriptions.${modalType}.descriptionFreeTrialStart`;
        } else {
          return `subscriptions.${modalType}.descriptionFreeTrialSubscribe`;
        }
      } else if (this.isFree) {
        return `subscriptions.${modalType}.descriptionFreeSubscription`;
      } else {
        return `subscriptions.${modalType}.description`;
      }
    };
  })
}).reopenClass({
  url: '/api/v1/subscriptions',
  subscribedStatuses: SubscribedStatuses,
  resubscribableStatuses: ResubscribableStatuses,
  unsubscribedStatuses: UnsubscribedStatuses,
  productTypes: ProductTypes,
  paymentMethods: PaymentMethods,
  previouslySubscribedStatuses: PreviouslySubscribedStatuses,
  Statuses,
  _modelNameForMigration: 'subscription'
});